import {
  CarInfo,
  CarManager,
  Cell,
  FacilityManager,
  HouseManager,
  INDUSTRY_CONFIGS,
  SurfaceAnt,
  TerrainGrid,
} from '@antification/core';
import { ParkingtonTool, type SelectedEntity, type HoveredEntity, type ParkingtonState } from '../ParkingtonTypes';
import { Camera } from './Camera';

/**
 * Context needed for highlight rendering.
 */
export interface HighlightRenderContext {
  camera: Camera;
  grid: TerrainGrid;
  cars: CarManager;
  houses: HouseManager;
  /** The federal estate, for the one word a sealed cell's tooltip has to add. */
  facilities: FacilityManager;
  state: ParkingtonState;
  surfaceAnts: () => Iterable<SurfaceAnt>;
  getBrushSize: () => number;
}

/**
 * Result of rendering the selection pane. Used by orchestrator for hit-testing.
 */
export interface SelectionPaneResult {
  closeArea: { x: number; y: number; w: number; h: number } | null;
  entityExists: boolean;
}

/**
 * Renders hover highlights, tooltips, selection highlights, and selection pane.
 */
export class HighlightRenderer {
  /**
   * Render hover highlight (dispatches based on current tool).
   */
  renderHoverHighlight(ctx: CanvasRenderingContext2D, rctx: HighlightRenderContext): void {
    const scale = rctx.camera.getScale();
    const w = rctx.camera.cellWidth * scale;
    const h = rctx.camera.cellHeight * scale;

    if (rctx.state.currentTool === ParkingtonTool.SELECT) {
      this.renderSelectToolHighlight(ctx, rctx, scale, w, h);
      return;
    }

    if (rctx.state.currentTool === ParkingtonTool.PRECISION || rctx.state.currentTool === ParkingtonTool.HIGHWAY) {
      this.renderBrushToolHighlight(ctx, rctx, scale, w, h);
      return;
    }
  }

  /**
   * Render highlights for SELECT tool mode.
   */
  private renderSelectToolHighlight(
    ctx: CanvasRenderingContext2D,
    rctx: HighlightRenderContext,
    scale: number,
    w: number,
    h: number
  ): void {
    if (rctx.state.selectedEntity) {
      this.renderEntitySelectionHighlight(ctx, rctx, rctx.state.selectedEntity, scale, w, h, true);
    }

    if (rctx.state.hoveredEntity) {
      const isSameAsSelected = rctx.state.selectedEntity &&
        rctx.state.selectedEntity.type === rctx.state.hoveredEntity.type &&
        rctx.state.selectedEntity.id === rctx.state.hoveredEntity.id;

      if (!isSameAsSelected) {
        this.renderEntitySelectionHighlight(ctx, rctx, rctx.state.hoveredEntity, scale, w, h, false);
      }
    }

    // Show simple cell highlight when no entity is hovered
    if (!rctx.state.hoveredEntity && rctx.state.hoveredCell) {
      const centerScreen = rctx.camera.gridToScreen(rctx.state.hoveredCell.x, rctx.state.hoveredCell.y);
      ctx.strokeStyle = 'rgba(45, 90, 123, 0.5)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(centerScreen.x, centerScreen.y - h / 2);
      ctx.lineTo(centerScreen.x + w / 2, centerScreen.y);
      ctx.lineTo(centerScreen.x, centerScreen.y + h / 2);
      ctx.lineTo(centerScreen.x - w / 2, centerScreen.y);
      ctx.closePath();
      ctx.stroke();
    }
  }

  /**
   * Render selection/hover highlight for an entity.
   */
  private renderEntitySelectionHighlight(
    ctx: CanvasRenderingContext2D,
    rctx: HighlightRenderContext,
    entity: SelectedEntity | HoveredEntity,
    scale: number,
    w: number,
    h: number,
    isSelected: boolean
  ): void {
    if (entity.type === 'car') {
      const carInfo = rctx.cars.getCarInfoById(entity.id);
      if (!carInfo) return;

      const carScreen = rctx.camera.gridToScreen(carInfo.gridX, carInfo.gridY);

      ctx.strokeStyle = isSelected ? '#e8a03c' : '#4a90a4';
      ctx.lineWidth = isSelected ? 3 : 2;
      ctx.setLineDash(isSelected ? [] : [4, 4]);
      ctx.beginPath();
      ctx.moveTo(carScreen.x, carScreen.y - h / 2 - 4);
      ctx.lineTo(carScreen.x + w / 2 + 4, carScreen.y);
      ctx.lineTo(carScreen.x, carScreen.y + h / 2 + 4);
      ctx.lineTo(carScreen.x - w / 2 - 4, carScreen.y);
      ctx.closePath();
      ctx.stroke();
      ctx.setLineDash([]);

      if (!isSelected) {
        ctx.fillStyle = 'rgba(74, 144, 164, 0.2)';
        ctx.beginPath();
        ctx.moveTo(carScreen.x, carScreen.y - h / 2 - 4);
        ctx.lineTo(carScreen.x + w / 2 + 4, carScreen.y);
        ctx.lineTo(carScreen.x, carScreen.y + h / 2 + 4);
        ctx.lineTo(carScreen.x - w / 2 - 4, carScreen.y);
        ctx.closePath();
        ctx.fill();

        ctx.fillStyle = 'rgba(50, 50, 50, 0.85)';
        ctx.font = '10px "Segoe UI", system-ui, sans-serif';
        const hintText = 'Click to select';
        const hintWidth = ctx.measureText(hintText).width + 12;
        ctx.beginPath();
        ctx.roundRect(carScreen.x - hintWidth / 2, carScreen.y - h / 2 - 24, hintWidth, 16, 3);
        ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(hintText, carScreen.x, carScreen.y - h / 2 - 16);
      }

    } else if (entity.type === 'building') {
      const house = rctx.houses.getHouseById(entity.id);
      if (!house) return;

      ctx.strokeStyle = isSelected ? '#e8a03c' : '#4a90a4';
      ctx.lineWidth = isSelected ? 3 : 2;
      ctx.setLineDash(isSelected ? [] : [4, 4]);

      for (const cell of house.footprint) {
        const cellX = house.gridX + cell.dx;
        const cellY = house.gridY + cell.dy;
        const cellScreen = rctx.camera.gridToScreen(cellX, cellY);

        ctx.beginPath();
        ctx.moveTo(cellScreen.x, cellScreen.y - h / 2);
        ctx.lineTo(cellScreen.x + w / 2, cellScreen.y);
        ctx.lineTo(cellScreen.x, cellScreen.y + h / 2);
        ctx.lineTo(cellScreen.x - w / 2, cellScreen.y);
        ctx.closePath();

        if (!isSelected) {
          ctx.fillStyle = 'rgba(74, 144, 164, 0.15)';
          ctx.fill();
        }
        ctx.stroke();
      }
      ctx.setLineDash([]);

      if (!isSelected) {
        const houseScreen = rctx.camera.gridToScreen(house.gridX, house.gridY);
        ctx.fillStyle = 'rgba(50, 50, 50, 0.85)';
        ctx.font = '10px "Segoe UI", system-ui, sans-serif';
        const hintText = 'Click to select';
        const hintWidth = ctx.measureText(hintText).width + 12;
        ctx.beginPath();
        ctx.roundRect(houseScreen.x - hintWidth / 2, houseScreen.y - h / 2 - 24, hintWidth, 16, 3);
        ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(hintText, houseScreen.x, houseScreen.y - h / 2 - 16);
      }

    } else if (entity.type === 'portal') {
      const portalCell = rctx.state.portalCells[entity.id];
      if (!portalCell) return;

      const portalScreen = rctx.camera.gridToScreen(portalCell.x, portalCell.y);

      const colonyColors = [
        { r: 139, g: 90, b: 43 },
        { r: 60, g: 80, b: 60 },
        { r: 180, g: 60, b: 60 },
      ];
      const colors = colonyColors[entity.id] || colonyColors[0];

      ctx.strokeStyle = isSelected ? '#e8a03c' : `rgb(${colors.r}, ${colors.g}, ${colors.b})`;
      ctx.lineWidth = isSelected ? 4 : 3;
      ctx.setLineDash(isSelected ? [] : [6, 4]);
      ctx.beginPath();
      ctx.arc(portalScreen.x, portalScreen.y, 18 * scale, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);

      if (!isSelected) {
        ctx.fillStyle = `rgba(${colors.r}, ${colors.g}, ${colors.b}, 0.2)`;
        ctx.beginPath();
        ctx.arc(portalScreen.x, portalScreen.y, 16 * scale, 0, Math.PI * 2);
        ctx.fill();
      }

      const colonyNames = ['Brown Colony', 'Dark Colony', 'Red Colony'];
      const colonyName = colonyNames[entity.id] || `Colony ${entity.id + 1}`;
      ctx.fillStyle = 'rgba(50, 50, 50, 0.9)';
      ctx.font = '10px "Segoe UI", system-ui, sans-serif';
      const hintText = isSelected ? `${colonyName} selected` : `Click to select ${colonyName}`;
      const hintWidth = ctx.measureText(hintText).width + 12;
      ctx.beginPath();
      ctx.roundRect(portalScreen.x - hintWidth / 2, portalScreen.y - 35 * scale, hintWidth, 16, 3);
      ctx.fill();
      ctx.fillStyle = '#fff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(hintText, portalScreen.x, portalScreen.y - 35 * scale + 8);
    }
  }

  /**
   * Render highlights for brush tools (Precision/Highway).
   */
  private renderBrushToolHighlight(
    ctx: CanvasRenderingContext2D,
    rctx: HighlightRenderContext,
    scale: number,
    w: number,
    h: number
  ): void {
    if (!rctx.state.hoveredCell) return;

    const brushSize = rctx.getBrushSize();
    const radius = Math.floor(brushSize / 2);

    ctx.strokeStyle = '#2d5a7b';
    ctx.lineWidth = 2;

    for (let dy = -radius; dy <= radius; dy++) {
      for (let dx = -radius; dx <= radius; dx++) {
        if (brushSize === 1 || dx * dx + dy * dy <= radius * radius + 1) {
          const cellX = rctx.state.hoveredCell.x + dx;
          const cellY = rctx.state.hoveredCell.y + dy;
          const cellScreen = rctx.camera.gridToScreen(cellX, cellY);

          ctx.beginPath();
          ctx.moveTo(cellScreen.x, cellScreen.y - h / 2);
          ctx.lineTo(cellScreen.x + w / 2, cellScreen.y);
          ctx.lineTo(cellScreen.x, cellScreen.y + h / 2);
          ctx.lineTo(cellScreen.x - w / 2, cellScreen.y);
          ctx.closePath();

          if (dx === 0 && dy === 0) {
            ctx.fillStyle = 'rgba(45, 90, 123, 0.3)';
            ctx.fill();
          }
          ctx.stroke();
        }
      }
    }

    // Show tooltip
    const carInfo = rctx.cars.getCarAt(rctx.state.hoveredCell.x, rctx.state.hoveredCell.y);
    const centerScreen = rctx.camera.gridToScreen(rctx.state.hoveredCell.x, rctx.state.hoveredCell.y);

    if (carInfo) {
      this.renderCarTooltip(ctx, rctx, centerScreen.x, centerScreen.y - 40, carInfo);
    } else {
      const cell = rctx.grid.getCell(rctx.state.hoveredCell.x, rctx.state.hoveredCell.y);
      if (cell) {
        this.renderTooltip(ctx, rctx, centerScreen.x, centerScreen.y - 40, cell);
      }
    }
  }

  /**
   * Render tooltip for a car.
   */
  private renderCarTooltip(
    ctx: CanvasRenderingContext2D,
    rctx: HighlightRenderContext,
    x: number,
    y: number,
    carInfo: CarInfo
  ): void {
    const padding = 8;
    const lineHeight = 16;

    const lines: string[] = [];

    const stateLabels: Record<string, string> = {
      'spawning': 'Appearing...',
      'driving': 'Driving',
      'parked': 'Parked',
      'despawning': 'Leaving...',
    };
    lines.push(`Status: ${stateLabels[carInfo.state] || carInfo.state}`);

    if (carInfo.mission !== 'none') {
      const missionLabels: Record<string, string> = {
        'picking_up': 'Picking up passenger',
        'transporting': 'Transporting passenger',
      };
      lines.push(missionLabels[carInfo.mission] || carInfo.mission);

      if (carInfo.passengerFamilyId !== null) {
        const family = rctx.houses.getFamily(carInfo.passengerFamilyId);
        if (family) {
          lines.push(`The ${family.name}s`);
          const homeHouse = rctx.houses.getHouseById(family.homeHouseId);
          lines.push(`Home: (${homeHouse?.gridX ?? '?'}, ${homeHouse?.gridY ?? '?'})`);

          if (carInfo.mission === 'picking_up' && carInfo.missionPickupHouseId !== null) {
            const pickupHouse = rctx.houses.getHouseById(carInfo.missionPickupHouseId);
            lines.push(`Pickup: (${pickupHouse?.gridX ?? '?'}, ${pickupHouse?.gridY ?? '?'})`);
          }

          // A commute names a gate rather than a building, and the tooltip says
          // so — it is the only place on screen a mayor can see that a car full
          // of their families is on its way to somebody else's machine.
          if (carInfo.missionDestinationFacilityId !== null) {
            const gate = rctx.state.facilities.gateOf(carInfo.missionDestinationFacilityId);
            lines.push(`Dest: (${gate?.x ?? '?'}, ${gate?.y ?? '?'}) [shift]`);
          } else if (carInfo.missionDestinationHouseId !== null) {
            const destHouse = rctx.houses.getHouseById(carInfo.missionDestinationHouseId);
            const destType = destHouse ? (destHouse as any).occupant : 'unknown';
            lines.push(`Dest: (${destHouse?.gridX ?? '?'}, ${destHouse?.gridY ?? '?'}) [${destType}]`);
          }
        }
      }
    } else {
      lines.push('No passengers');
    }

    const boxWidth = 180;
    const boxHeight = lines.length * lineHeight + padding * 2;

    let tooltipX = x - boxWidth / 2;
    let tooltipY = y - boxHeight;

    tooltipX = Math.max(10, Math.min(rctx.state.width - boxWidth - 10, tooltipX));
    tooltipY = Math.max(60, tooltipY);

    ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
    ctx.strokeStyle = '#4a90a4';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.roundRect(tooltipX, tooltipY, boxWidth, boxHeight, 4);
    ctx.fill();
    ctx.stroke();

    ctx.fillStyle = '#333';
    ctx.font = '11px "Segoe UI", system-ui, sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';

    lines.forEach((line, i) => {
      ctx.fillText(line, tooltipX + padding, tooltipY + padding + i * lineHeight);
    });
  }

  /**
   * Render tooltip for a cell.
   */
  private renderTooltip(
    ctx: CanvasRenderingContext2D,
    rctx: HighlightRenderContext,
    x: number,
    y: number,
    cell: ReturnType<TerrainGrid['getCell']>
  ): void {
    if (!cell) return;

    const padding = 8;
    const lineHeight = 16;

    const getPotentialLabel = (v: number) => v < 75 ? 'Good' : v < 85 ? 'Very Good' : v < 95 ? 'Excellent' : 'Outstanding';

    const getNextAction = (state: string) => {
      switch (state) {
        case 'natural': return 'Click to survey';
        case 'surveyed': return 'Click to clear';
        case 'cleared': return 'Click to grade';
        case 'graded': return 'Click to pour concrete';
        case 'poured': return 'Curing...';
        case 'cured': return 'Click to paint';
        case 'painted': return 'Complete!';
        // A facility's footprint. The mayor's brush does nothing here and the
        // panel says so in the register the rest of this tooltip is written in —
        // three bland words for twenty-four cells nobody asked them about. Four,
        // while the lorries are still coming, and the fourth is the only word the
        // interface ever offers about what is being built or when it will be done.
        case 'sealed': {
          const id = rctx.facilities.covers(
            rctx.state.hoveredCell!.x,
            rctx.state.hoveredCell!.y
          );
          const under = id !== null && !rctx.facilities.isOperating(id);
          return under ? 'Federal parcel — under construction' : 'Federal parcel';
        }
        default: return '';
      }
    };

    const propValue = rctx.houses.getPropertyValue(rctx.state.hoveredCell!.x, rctx.state.hoveredCell!.y);
    const lines = [
      `Status: ${cell.state.toUpperCase()}`,
      getNextAction(cell.state),
      `Potential: ${cell.developmentPotential}% (${getPotentialLabel(cell.developmentPotential)})`,
      `Property Value: ${propValue}`,
    ];

    // Over a federal parcel, the two lines that are actually about this cell:
    // how many of your families work here, and the one verb you have. Nothing
    // says whose facility it is or what it does, because nothing knows.
    const facilityId = rctx.state.facilities.covers(
      rctx.state.hoveredCell!.x, rctx.state.hoveredCell!.y
    );
    if (facilityId !== null) {
      const facility = rctx.state.facilities.getFacility(facilityId);
      const posts = facility ? rctx.state.facilities.staffFor(facility.type) : 0;
      const staffing = rctx.state.labour.getFacilityStaffing(facilityId, posts);
      if (posts > 0) {
        lines.push(`Staff: ${staffing.working}/${staffing.posts} of your families`);
        lines.push(staffing.withdrawn ? 'W: send them back' : 'W: withdraw staffing');
      } else {
        // A facility that needs nobody. The line says what it is rather than what
        // you can do about it, because there is nothing you can do about it.
        lines.push('Staff: none required');
      }
    }

    // What the district has already bought for this cell. One line, in the flattest
    // wording available: a covenant reads as a status and not as a virtue, and the
    // crossing line says what is under the road without saying who uses it.
    //
    // The hedgerow is the only one with a number, and the number is a countdown
    // rather than a benefit — how long until there is a bush here, which is the one
    // fact about a sapling that a mayor might act on.
    const hx = rctx.state.hoveredCell!.x;
    const hy = rctx.state.hoveredCell!.y;
    if (rctx.state.mercy.isDeeded(hx, hy)) {
      lines.push('Deeded wild — not developable');
    }
    if (rctx.state.mercy.isSapling(hx, hy)) {
      lines.push('Hedgerow planted — growing');
    }
    if (rctx.state.mercy.shelters(hx, hy)) {
      lines.push('Undercrossing beneath');
    }

    const boxWidth = 150;
    const boxHeight = lines.length * lineHeight + padding * 2;

    let tooltipX = x - boxWidth / 2;
    let tooltipY = y - boxHeight;

    tooltipX = Math.max(10, Math.min(rctx.state.width - boxWidth - 10, tooltipX));
    tooltipY = Math.max(60, tooltipY);

    ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
    ctx.strokeStyle = '#2d5a7b';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.roundRect(tooltipX, tooltipY, boxWidth, boxHeight, 4);
    ctx.fill();
    ctx.stroke();

    ctx.fillStyle = '#333';
    ctx.font = '11px "Segoe UI", system-ui, sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';

    lines.forEach((line, i) => {
      ctx.fillText(line, tooltipX + padding, tooltipY + padding + i * lineHeight);
    });
  }

  /**
   * Render the selection detail pane. Returns close area for hit-testing.
   */
  renderSelectionPane(ctx: CanvasRenderingContext2D, rctx: HighlightRenderContext): SelectionPaneResult {
    if (!rctx.state.selectedEntity || rctx.state.currentTool !== ParkingtonTool.SELECT) {
      return { closeArea: null, entityExists: true };
    }

    const panelWidth = 220;
    const panelX = 20;
    const panelY = 120;
    const padding = 12;
    const lineHeight = 18;

    let title = '';
    const infoLines: string[] = [];
    const actions: { label: string; enabled: boolean }[] = [];

    if (rctx.state.selectedEntity.type === 'car') {
      const carInfo = rctx.cars.getCarInfoById(rctx.state.selectedEntity.id);
      if (!carInfo) {
        return { closeArea: null, entityExists: false };
      }

      title = `🚗 Vehicle #${rctx.state.selectedEntity.id}`;

      const stateLabels: Record<string, string> = {
        'spawning': 'Appearing',
        'driving': 'Driving',
        'parked': 'Parked',
        'despawning': 'Leaving',
      };
      infoLines.push(`Status: ${stateLabels[carInfo.state] || carInfo.state}`);

      if (carInfo.mission !== 'none') {
        const missionLabels: Record<string, string> = {
          'picking_up': 'Picking up passenger',
          'transporting': 'Transporting',
        };
        infoLines.push(`Mission: ${missionLabels[carInfo.mission] || carInfo.mission}`);

        if (carInfo.passengerFamilyId !== null) {
          const family = rctx.houses.getFamily(carInfo.passengerFamilyId);
          if (family) {
            infoLines.push(`Passenger: The ${family.name}s`);
          }
        }
      } else {
        infoLines.push('No passengers');
      }

      actions.push({ label: 'Track Vehicle', enabled: true });
      actions.push({ label: 'View Route', enabled: carInfo.mission !== 'none' });

    } else if (rctx.state.selectedEntity.type === 'building') {
      const house = rctx.houses.getHouseById(rctx.state.selectedEntity.id);
      if (!house) {
        return { closeArea: null, entityExists: false };
      }

      const isCorpBuilding = house.occupant === 'corporation';

      if (isCorpBuilding && house.industryType) {
        const config = INDUSTRY_CONFIGS[house.industryType];
        title = `${config.icon} ${config.label} #${house.id}`;
      } else if (isCorpBuilding) {
        title = `🏢 Corporation #${house.id}`;
      } else {
        // Show family name for residential
        const families = rctx.houses.getAllFamilies();
        const homeFamily = families.find(f => f.homeHouseId === house.id);
        title = homeFamily ? `🏠 The ${homeFamily.name}s` : `🏠 House #${house.id}`;
      }

      infoLines.push(`Type: ${isCorpBuilding ? 'Corporate' : 'Residential'}`);
      infoLines.push(`Status: ${house.built ? 'Built' : `Building (${Math.floor(house.buildProgress * 100)}%)`}`);

      if (isCorpBuilding) {
        infoLines.push(`Revenue: $${Math.floor(house.revenue)}`);
        infoLines.push(`Taxes Paid: $${Math.floor(house.taxesPaid)}`);
        const subCount = rctx.houses.getSubscriberCount(house.id);
        infoLines.push(`Subscribers: ${subCount}`);
        const shareInfo = rctx.houses.getHouseShareholderInfo(house.id);
        infoLines.push(`Shareholders: ${shareInfo.totalShares} shares`);
        const divPaid = rctx.houses.getHouseDividendsPaid(house.id);
        if (divPaid > 0) infoLines.push(`Dividends Paid: $${Math.floor(divPaid)}`);
      } else if (house.familyHome) {
        // Find the family for this house
        const families = rctx.houses.getAllFamilies();
        const homeFamily = families.find(f => f.homeHouseId === house.id);
        if (homeFamily) {
          infoLines.push(`Cash: $${Math.floor(homeFamily.cash)}`);
          const subCount = rctx.houses.getFamilySubscriptionCount(homeFamily.id);
          infoLines.push(`Subscriptions: ${subCount}/6`);
          const portfolioCount = rctx.houses.getFamilyShareCount(homeFamily.id);
          if (portfolioCount > 0) infoLines.push(`Portfolio: shares in ${portfolioCount} corps`);
          // How this household is actually doing, on the one screen in the game
          // that is about one household. The score is always shown once it has
          // been measured; the two exposures appear only when they are not zero,
          // so a family with nothing wrong reads exactly as it did before there
          // was a measure. Nothing here says what the mayor ought to do.
          const qol = rctx.state.welfare.qolOf(homeFamily.id);
          if (qol !== null) infoLines.push(`Quality of Life: ${qol.toFixed(2)}`);
          const exposure = rctx.state.welfare.exposureOf(homeFamily.id);
          if (exposure.dark > 0) infoLines.push(`In the dark: ${exposure.dark} cycles`);
          if (exposure.dry > 0) infoLines.push(`Without water: ${exposure.dry} cycles`);
          // Patience spent against patience held. The second number is a
          // property of this household and not of the district: two families
          // in identical trouble give up at different times, which is what
          // stops a shortage taking a whole street in one cycle.
          const spent = homeFamily.hardshipCycles;
          if (spent > 0) {
            const tolerance = rctx.state.welfare.toleranceOf(homeFamily.id);
            infoLines.push(`Thinking of leaving: ${spent.toFixed(1)} / ${tolerance.toFixed(1)}`);
          }
        }
      }

      infoLines.push(`Floors: ${house.floors}`);

      actions.push({ label: 'View Details', enabled: true });
      actions.push({ label: 'Upgrade', enabled: false });

    } else if (rctx.state.selectedEntity.type === 'portal') {
      const colonyIndex = rctx.state.selectedEntity.id;
      const portalCell = rctx.state.portalCells[colonyIndex];
      if (!portalCell) {
        return { closeArea: null, entityExists: false };
      }

      const colonyNames = ['Brown Colony', 'Dark Colony', 'Red Colony'];
      const colonyName = colonyNames[colonyIndex] || `Colony ${colonyIndex + 1}`;
      title = `🕳️ ${colonyName} Portal`;

      infoLines.push(`Colony: ${colonyName}`);
      infoLines.push(`Position: (${portalCell.x}, ${portalCell.y})`);

      let surfaceAntCount = 0;
      for (const ant of rctx.surfaceAnts()) {
        if (ant.colonyIndex === colonyIndex) {
          surfaceAntCount++;
        }
      }
      infoLines.push(`Surface Ants: ${surfaceAntCount}`);

      actions.push({ label: 'View Colony (Tab)', enabled: true });
      actions.push({ label: 'Track Foragers', enabled: false });
    }

    // Calculate panel height
    const titleHeight = 28;
    const infoHeight = infoLines.length * lineHeight;
    const actionsSectionHeight = actions.length > 0 ? 20 + actions.length * 28 : 0;
    const panelHeight = padding + titleHeight + infoHeight + actionsSectionHeight + padding;

    // Draw panel background
    ctx.fillStyle = 'rgba(255, 255, 255, 0.98)';
    ctx.strokeStyle = '#2d5a7b';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.roundRect(panelX, panelY, panelWidth, panelHeight, 8);
    ctx.fill();
    ctx.stroke();

    // Title
    ctx.fillStyle = '#2d5a7b';
    ctx.font = 'bold 13px "Segoe UI", system-ui, sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText(title, panelX + padding, panelY + padding);

    // Close button
    const closeX = panelX + panelWidth - padding - 16;
    const closeY = panelY + padding;
    ctx.fillStyle = '#888';
    ctx.font = '14px "Segoe UI", system-ui, sans-serif';
    ctx.fillText('✕', closeX, closeY);

    const closeArea = { x: closeX - 4, y: closeY - 4, w: 24, h: 24 };

    // Info lines
    ctx.fillStyle = '#333';
    ctx.font = '11px "Segoe UI", system-ui, sans-serif';
    let currentY = panelY + padding + titleHeight;

    for (const line of infoLines) {
      ctx.fillText(line, panelX + padding, currentY);
      currentY += lineHeight;
    }

    // Actions section
    if (actions.length > 0) {
      currentY += 8;
      ctx.fillStyle = '#666';
      ctx.font = 'bold 10px "Segoe UI", system-ui, sans-serif';
      ctx.fillText('ACTIONS', panelX + padding, currentY);
      currentY += 16;

      for (const action of actions) {
        const actionX = panelX + padding;
        const actionWidth = panelWidth - padding * 2;
        const actionHeight = 24;

        ctx.fillStyle = action.enabled ? '#f0f4f8' : '#f8f8f8';
        ctx.strokeStyle = action.enabled ? '#ccd' : '#ddd';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.roundRect(actionX, currentY, actionWidth, actionHeight, 4);
        ctx.fill();
        ctx.stroke();

        ctx.fillStyle = action.enabled ? '#333' : '#aaa';
        ctx.font = '11px "Segoe UI", system-ui, sans-serif';
        ctx.textBaseline = 'middle';
        ctx.fillText(action.label, actionX + 8, currentY + actionHeight / 2);

        currentY += actionHeight + 4;
      }
    }

    return { closeArea, entityExists: true };
  }
}
